/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TextField from '@mui/material/TextField';
import { getIn } from 'formik';
import type { AnyAction, Dispatch } from 'redux';
import styled from 'styled-components';

import { updateField } from '../../store/customer';
import { clearFieldError, setErrorFields } from '../../store/fieldError';
import { contactDeliveryType } from '../../utils/addressHelpers';
import { CARD_TYPE } from '../../utils/constants';
import { validateCardEmail, validateEmail } from '../../utils/validation';
import { ErrorFieldCheck } from '../ErrorValidation';
import Tag from '../Tag';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 16px;
  padding-top: 4px;
`;

const Input = styled.input`
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  flex: 1 1 160px;
  min-width: 120px;
`;

const Footer = styled.div`
  margin: 3px 0;
  font-size: 12px;
  font-weight: lighter;

  a {
    color: var(--cargill-green);
  }
`;

interface FieldInputWithTagsProps {
  formik: any;
  store: any;
  name: string;
  required?: boolean;
  type?: string;
  label?: string;
  footer?: string;
  placeholder?: string;
  hasGroupError?: boolean;
  dispatch: Dispatch<AnyAction>;
  apiService: any;
  onChange?: (args: {
    value: unknown;
    dispatch: Dispatch<AnyAction>;
    formik: any;
    apiService: any;
    store: any;
  }) => void;
  disableAutoSave?: boolean;
  onAddTag?: (args: { store: any; dispatch: Dispatch<AnyAction> }) => void;
}

const TagList = memo(
  ({
    tags,
    hasError,
    name,
    t,
    onRemove
  }: {
    tags: string[];
    hasError: boolean;
    name: string;
    t: (key: string) => string;
    onRemove: (tag: string) => void;
  }) => {
    const isValid = CARD_TYPE.some(item => name.includes(item));

    return (
      Array.isArray(tags) &&
      tags.map(item => {
        const errors: string[] = [];

        if (hasError) {
          isValid ? validateCardEmail(item, t, errors) : validateEmail(item, t, errors);
        }

        return <Tag key={item} label={item} handleRemove={onRemove} hasError={hasError && errors.length > 0} />;
      })
    );
  }
);

function TagInputStable(props: any): JSX.Element {
  const {
    inputRef: muiInputRef,
    className,
    tags,
    hasError,
    isEmpty,
    onWrapperClick,
    onRemoveTag,
    t,
    name,
    ...rest
  } = props;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 8,
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'text',
        padding: 8,
        paddingLeft: isEmpty ? 4 : 8
      }}
      onClick={onWrapperClick}
    >
      <TagList tags={tags} hasError={hasError} name={name} t={t} onRemove={onRemoveTag} />

      <Input ref={muiInputRef} className={className} {...rest} style={{ padding: 0, alignSelf: 'center' }} />
    </div>
  );
}

function FieldInputWithTags({
  formik,
  store,
  name,
  required,
  type,
  label,
  footer,
  placeholder,
  hasGroupError,
  dispatch,
  apiService,
  onChange,
  disableAutoSave,
  onAddTag = () => {}
}: FieldInputWithTagsProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isTouch, setIsTouch] = useState(false);
  const { t } = useTranslation();

  const handleUpdate = (value: string[]) => {
    contactDeliveryType(name, dispatch);
    dispatch(updateField({ name, value }));

    formik.form.setFieldValue(name, value).then((error: any) => {
      apiService.updateCustomer(store.customer, dispatch);

      if (getIn(error, name) === undefined) {
        dispatch(clearFieldError(name));
      } else {
        dispatch(setErrorFields(error));
      }
    });
  };

  const handleRemoveTag = (tag: string) => {
    const updatedValue = formik.field.value.filter((item: string) => item !== tag);
    handleUpdate(updatedValue);
  };

  const handleAddTag = () => {
    setIsTouch(true);

    let trimmedInputValue = inputValue;

    if (formik.field.name?.includes('email')) {
      trimmedInputValue = inputValue.replace(/ /g, '');
    }

    if (trimmedInputValue !== '') {
      const val = trimmedInputValue.toLowerCase();

      if (!formik.field.value.includes(val)) {
        handleUpdate([...formik.field.value, val]);
      }

      onAddTag({ store, dispatch });
      setInputValue('');

      if (!disableAutoSave) {
        apiService.updateCustomer(store.customer, dispatch);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);

    onChange?.({
      value,
      dispatch,
      formik,
      apiService,
      store
    });
  };

  const errorMessage = getIn(formik.form.errors, name);
  const hasError = errorMessage !== undefined && !(errorMessage instanceof Object);

  return (
    <Wrapper className="field-input-with-tags">
      <TextField
        variant="outlined"
        fullWidth
        label={label || ''}
        required={!!required}
        error={(hasError && isTouch) || hasGroupError}
        type={type}
        name={name}
        value={inputValue}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onChange={handleOnChange}
        onBlur={handleAddTag}
        inputRef={inputRef}
        sx={{
          m: 0,
          '& .MuiOutlinedInput-root': { p: 0 },
          '& .MuiOutlinedInput-input': { padding: 0 }
        }}
        slotProps={{
          input: {
            inputComponent: TagInputStable as any
          },
          htmlInput: {
            'aria-required': !!required,
            tags: formik.field.value,
            hasError: hasError || isTouch,
            isEmpty: (formik.field.value?.length || 0) === 0 && !inputValue,
            onWrapperClick: () => inputRef.current?.focus(),
            onRemoveTag: handleRemoveTag,
            t,
            name
          }
        }}
      />

      <ErrorFieldCheck hasError={hasError} isTouch={isTouch} errorMessage={errorMessage} setIsTouch={setIsTouch} />

      {footer && <Footer>{footer}</Footer>}
    </Wrapper>
  );
}

export default FieldInputWithTags;
